/* =============================================================
   Add-to-cart upsell popup

   Listens for `ajaxProduct:added` and offers more products to add in one go.

   Primary source is the colour just added — other styles in that colour, taken from a collection
   and matched on the colour in each product's title. This is the same_color_collection logic of
   snippets/product-complementary.liquid, which the PDP's "Pairs great with" block uses: a real
   collection is the input, no collection has to exist per colour.

   The merchant's "Color pairs" setting widens that to a second colour — the same setting, format
   and intent as the Featured Slider's. Paired colours rank below the colour added, so they fill
   the popup out rather than crowding out the shade the shopper actually bought.

   On product pages the section inlines that payload already filtered (a [data-upsell-source] block
   rendered by 'upsell-popup-products'), so no request is needed. Anywhere else the source collection
   is fetched from /collections/{handle}?view=upsell, which renders the same snippet unfiltered, and
   itemsFromNodes() does the colour matching here.

   Fallback source is the other colourways of the same style, read from
   window.theme.styleSwatchStore (published by 'new_swatch_logic'), for when nothing in the
   collection matches. Merchants can also pick that source outright.

   Rendered by: sections/upsell-popup.liquid
   ============================================================= */

import { formatMoney as miscFormatMoney } from 'util.misc';

const STYLE_READY_TIMEOUT = 1500;
// How long the cart drawer is held while we work out whether there is anything to offer. Long
// enough for the ?view=upsell fetch on a slow connection, short enough that a hung request does
// not read as a dead add-to-cart button.
const CART_GATE_TIMEOUT = 800;

// Stamped on every line this popup adds, so upsell-driven revenue can be told apart from the
// add that triggered the popup — in Liquid, the Ajax cart, order webhooks, Flow and the Admin API.
//
// Underscore-prefixed per Shopify's private line-item-property convention: checkout and the order
// status page hide it, and the two places this theme prints properties —
// snippets/cart-drawer-v2.liquid and templates/customers/order.liquid — already skip keys starting
// with "_", so it stays out of the shopper's way without further changes.
//
// Note this makes a popup-added line distinct from the same variant added anywhere else: Shopify
// keys cart lines on variant + properties, so the two sit as separate lines rather than merging.
// That is what keeps the attribution intact.
const UPSELL_LINE_PROPERTY_KEY = '_added_via';
const UPSELL_LINE_PROPERTY_VALUE = 'upsell-popup';

const parseJsonAttr = function (el, name, fallback) {
  try {
    const raw = el && el.getAttribute(name);
    return raw ? JSON.parse(raw) : fallback;
  } catch (err) {
    return fallback;
  }
};

const variantOptions = function (variant) {
  if (Array.isArray(variant && variant.options)) return variant.options;
  return [variant && variant.option1, variant && variant.option2, variant && variant.option3].filter(Boolean);
};

const variantMatchesSelectedOptions = function (variant, selectedOptions, skipIndex) {
  const options = variantOptions(variant);
  return selectedOptions.every(function (value, index) {
    if (index === skipIndex || value == null || value === '') return true;
    return options[index] === value;
  });
};

const splitStyleTitle = function (title) {
  const pieces = String(title || '').split('-').map(function (part) { return part.trim(); }).filter(Boolean);
  return pieces[0] || String(title || '');
};

const splitStyleLabel = function (title) {
  const pieces = String(title || '').split('-').map(function (part) { return part.trim(); }).filter(Boolean);
  if (pieces.length < 2) return '';
  return pieces[pieces.length - 1];
};

/* Everything after the FIRST "-" — matches the colour derivation in
   snippets/product-complementary.liquid, which keeps "Off - White" intact. */
const colorFromTitle = function (title) {
  const pieces = String(title || '').split('-');
  if (pieces.length < 2) return '';
  return pieces.slice(1).join('-').trim();
};

/* Ported verbatim from theme.js ProductRecommendations.normalizeColor so the popup and the
   complementary block agree on what counts as the same colour. */
const normalizeColor = function (value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/* Colour families: "Nightfall" and "Nightfall Gem" are the same colour story and belong in the
   same popup, but "Night" and "Nightfall" do not. Matching on a whole leading token — in either
   direction, so adding either one finds the other — keeps the family together without letting a
   shorter unrelated colour swallow a longer one. Mirrors the filter in 'upsell-popup-products'. */
const sameColorFamily = function (candidate, added) {
  if (!candidate || !added) return false;
  if (candidate === added) return true;
  return candidate.indexOf(added + '-') === 0 || added.indexOf(candidate + '-') === 0;
};

/* Merchant-configured colour pairs, one "A + B" per line (or ";"-separated) — the format the
   Featured Slider's own "Color pairs" setting uses. Bidirectional: either half finds the other.
   Normalized through normalizeColor so a merchant typing "Blue-Steel" still matches a title's
   "Blue Steel", which comparing raw strings would silently miss. */
const parseColorPairs = function (raw) {
  return String(raw || '')
    .split(/\r?\n|;/)
    .map(function (line) { return line.split('+'); })
    .filter(function (halves) { return halves.length >= 2; })
    .map(function (halves) { return [normalizeColor(halves[0]), normalizeColor(halves[1])]; })
    .filter(function (pair) { return pair[0] && pair[1] && pair[0] !== pair[1]; });
};

/* The colours this add will accept, ranked: the colour just added is always rank 0, then the
   colours paired with it in the order the merchant listed them. Ranking rather than plain
   filtering is what keeps the exact colour above its partners once itemsFromNodes() sorts.

   Family membership is deliberately NOT folded in here — it stays in sameColorFamily(), applied
   per rank below, so "Milk" is still reachable from "Milk Gem" whether or not a pair line
   mentions either one. */
const relatedColorRanks = function (addedHandle, rawPairs) {
  const ranks = new Map();
  if (!addedHandle) return ranks;

  ranks.set(addedHandle, 0);
  parseColorPairs(rawPairs).forEach(function (pair) {
    let partner = '';
    if (pair[0] === addedHandle) partner = pair[1];
    else if (pair[1] === addedHandle) partner = pair[0];
    if (!partner || ranks.has(partner)) return;
    ranks.set(partner, ranks.size);
  });

  return ranks;
};

/* Rank of the closest colour this candidate belongs to the family of, or -1 for no match. */
const colorRankFor = function (candidateHandle, ranks) {
  if (!candidateHandle) return -1;

  let best = -1;
  ranks.forEach(function (rank, handle) {
    if (best !== -1 && rank >= best) return;
    if (sameColorFamily(candidateHandle, handle)) best = rank;
  });
  return best;
};

const displayOptionText = function (value, optionName) {
  if (!String(optionName || '').trim().toLowerCase().includes('size')) return value;

  const normalized = String(value || '').trim().toLowerCase();
  const labels = {
    xsmall: 'XS',
    small: 'S',
    medium: 'M',
    large: 'L',
    'extra large': 'XL',
    'x-large': 'XL',
    xl: 'XL',
    xlarge: 'XL',
    'xx-large': 'XXL',
    xxl: 'XXL',
    xxlarge: 'XXL'
  };

  return labels[normalized] || value;
};

const formatMoney = function (cents) {
  const locale = window.locale || `${window.Shopify.locale}-${window.Shopify.country}`;
  const currency = window.currency || window.Shopify.currency.active;
  return miscFormatMoney({ locale, currency, value: cents });
};

/* Swatch anchors render `image_url` with no size parameter — never ship the original. */
const sizedImage = function (url, width) {
  if (!url) return '';
  return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
};

const parseSwatchProductData = function (source) {
  if (!source) return null;
  const title = source.getAttribute('data-product-title') || source.getAttribute('title') || '';
  return {
    id: source.getAttribute('data-related-product-id') || source.getAttribute('data-product-id') || '',
    title: title,
    styleTitle: splitStyleTitle(title),
    colorLabel: splitStyleLabel(title),
    url: source.getAttribute('data-swatch-url') || source.getAttribute('href') || '',
    price: Number.parseInt(source.getAttribute('data-product-price') || '0', 10) || 0,
    comparePrice: Number.parseInt(source.getAttribute('data-product-compare-price') || '0', 10) || 0,
    priceVaries: (source.getAttribute('data-product-price-varies') || '').toLowerCase() === 'true',
    available: (source.getAttribute('data-available') || '').toLowerCase() !== 'false',
    imageFirst: source.getAttribute('data-swatch-img-first') || '',
    options: parseJsonAttr(source, 'data-product-options', []),
    variants: parseJsonAttr(source, 'data-product-variants', []),
    optionIndexes: parseJsonAttr(source, 'data-hover-option-indexes', [])
      .map(function (index) { return Number.parseInt(index, 10); })
      .filter(function (index) { return Number.isFinite(index) && index >= 0; })
  };
};

/* The cart drawer opens on the same `ajaxProduct:added` this module listens for, and it binds
   first (theme.CartDrawer.init) so it cannot be pre-empted by listener order. Its patched handler
   defers the open by a microtask and skips it while this gate is held — every synchronous listener
   for that dispatch, including ours, has run by then, so claiming the add below is enough to keep
   the drawer from ever painting.

   Owned here on purpose: with the popup section disabled this asset never loads, the gate is
   absent, and the drawer takes its normal path. */
window.theme = window.theme || {};
window.theme.cartOpenGate = window.theme.cartOpenGate || (function () {
  let timer = null;

  return {
    /* onExpire runs if resolution stalls — the customer must always end up with a popup or a
       drawer, never neither. */
    hold: function (onExpire) {
      if (timer) clearTimeout(timer);
      timer = window.setTimeout(function () {
        timer = null;
        if (typeof onExpire === 'function') onExpire();
      }, CART_GATE_TIMEOUT);
    },

    isHeld: function () {
      return timer !== null;
    },

    release: function () {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
    }
  };
})();

if (!customElements.get('upsell-popup')) {
  class UpsellPopup extends HTMLElement {
    connectedCallback() {
      this.strings = {
        colorLabel: 'Color',
        sizeLabel: 'Size',
        soldOut: 'Sold out',
        addButton: 'Add selected to cart',
        addButtonWithCount: 'Add [count] to cart',
        errorIncomplete: 'Select a size for each item before adding to your cart.',
        errorAddFailed: 'Unable to add these items to your cart. Please try again.',
        fromText: 'From [price]',
        regularPrice: 'Regular price'
      };

      const stringsNode = this.querySelector('[data-upsell-i18n]');
      if (stringsNode) {
        try {
          Object.assign(this.strings, JSON.parse(stringsNode.textContent || '{}'));
        } catch (err) {
          console.warn('Unable to parse upsell popup i18n strings', err);
        }
      }

      this.maxProducts = Number.parseInt(this.getAttribute('data-max-products') || '6', 10) || 6;
      this.showSoldOut = (this.getAttribute('data-show-sold-out') || '').toLowerCase() === 'true';
      this.frequency = this.getAttribute('data-frequency') || 'every_add';
      this.source = this.getAttribute('data-source') || 'same_color';
      this.colorPairs = this.getAttribute('data-color-pairs') || '';
      // V2 ("Added to cart" + "Complete the look"): exact colour only, no same-style fallback, and
      // no popup at all when the colour has nothing to offer. See sections/upsell-popup.liquid.
      this.isV2 = this.getAttribute('data-design') === 'v2';
      if (this.isV2) {
        this.source = 'same_color';
        this.colorPairs = '';
      }

      this.list = this.querySelector('[data-upsell-list]');
      this.template = this.querySelector('[data-upsell-item-template]');
      this.panel = this.querySelector('.upsell-popup__panel');
      this.submitBtn = this.querySelector('[data-upsell-submit]');
      this.errorEl = this.querySelector('[data-upsell-error]');

      this.gate = window.theme.cartOpenGate;
      this.shownStyles = new Set();
      // Colourways already in the cart this page view — never offered back.
      this.addedProductIds = new Set();
      this.returnFocusTo = null;
      this.debug = new URLSearchParams(window.location.search).has('upsell_debug');
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.addedEl = this.querySelector('[data-upsell-added]');

      this.log('ready', {
        maxProducts: this.maxProducts,
        showSoldOut: this.showSoldOut,
        frequency: this.frequency,
        source: this.source,
        design: this.isV2 ? 'v2' : 'v1'
      });

      document.addEventListener('ajaxProduct:added', this.onProductAdded.bind(this), { signal });
      // The newer <product-form> element (snippets/form.product.liquid) never dispatches
      // ajaxProduct:added — it reports success on its own event instead.
      document.addEventListener('product-form:submit:success', this.onProductFormSuccess.bind(this), { signal });
      document.addEventListener('keydown', this.onKeydown.bind(this), { signal });
      document.addEventListener('keyup', this.onKeyup.bind(this), { signal });
      // The drawer we just closed re-focuses its opener 500ms later (theme.Drawers.close),
      // which would pull focus out of this dialog while it is still open.
      document.addEventListener('focusin', this.onFocusIn.bind(this), { signal });

      this.addEventListener('click', this.onClick.bind(this), { signal });
      this.addEventListener('change', this.onChange.bind(this), { signal });
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    /* ─── Trigger ─────────────────────────────────────────────── */

    log(message, data) {
      if (!this.debug) return;
      console.log('[upsell-popup] ' + message, data === undefined ? '' : data);
    }

    onProductFormSuccess(event) {
      const detail = event.detail || {};
      this.trigger(detail.response, null, event.target);
    }

    onProductAdded(event) {
      const detail = event.detail;

      // The 422 path in theme.js dispatches this event with no detail; our own
      // multi-add re-dispatches it with suppressUpsell so we never re-open.
      if (detail && detail.suppressUpsell) return;
      this.trigger(detail && detail.product, detail && detail.addToCartBtn, event.target);
    }

    /* The collection the add came from, when the surrounding section published one
       ([data-upsell-collection], set by the featured-collection sections). Beats the section's own
       fallback, which on a page with no collection of its own is the whole catalog — and the whole
       catalog is scanned only as deep as 'collection.upsell' emits, so a colour past that point
       reads as "nothing in this colour" and drops to the same-style fallback.

       Read off the section, not the card: new_swatch_logic rewrites card hrefs to the bare product
       URL the moment a swatch is picked, so a link's /collections/ prefix cannot be relied on. */
    originCollection(addToCartBtn, sourceEl) {
      const origin = addToCartBtn || sourceEl;
      if (!origin || typeof origin.closest !== 'function') return '';

      let anchor = origin;

      // The mobile size drawer is mounted on <body> (mountMobileDrawerToBody in new_swatch_logic),
      // so an add from there has no section above it. Hop back to the card that opened it — the
      // two share a data-mobile-drawer-owner id for exactly this reason.
      const drawer = origin.closest('[data-mobile-product-drawer][data-mobile-drawer-owner]');
      if (drawer && window.CSS && typeof CSS.escape === 'function') {
        const ownerId = drawer.getAttribute('data-mobile-drawer-owner');
        const card = ownerId && document.querySelector(
          '[data-mobile-drawer-owner="' + CSS.escape(ownerId) + '"]:not([data-mobile-product-drawer])'
        );
        if (card) anchor = card;
      }

      const section = anchor.closest('[data-upsell-collection]');
      return section ? (section.getAttribute('data-upsell-collection') || '').trim() : '';
    }

    /* Resolves the style just added from whichever shape the dispatcher used, falling
       back to the swatch container in the DOM the add came from. */
    trigger(product, addToCartBtn, sourceEl) {
      if (this.classList.contains('is-open')) return;
      if (!this.list || !this.template) {
        this.log('skipped: popup markup missing');
        return;
      }
      const line = product && product.items ? product.items[0] : product;
      let addedTitle = (line && (line.product_title || line.title)) || '';
      let addedId = String((line && line.product_id) || '');

      if (!addedTitle && sourceEl && sourceEl.closest) {
        const container = sourceEl.closest('.shopify-section, body');
        const swatches = container && container.querySelector('[data-style-key]');
        if (swatches) {
          addedTitle = swatches.getAttribute('data-product-title') || '';
          addedId = addedId || swatches.getAttribute('data-product-id') || '';
          if (!addedTitle) addedTitle = swatches.getAttribute('data-style-key') || '';
        }
      }

      const styleKey = splitStyleTitle(addedTitle);
      const colorValue = colorFromTitle(addedTitle);
      // product.id on a cart/add.js line item is the variant id — only product_id identifies
      // the colourway. Title is the fallback so the added product is never offered back.
      const added = { id: addedId, title: addedTitle };

      this.log('add-to-cart seen', {
        styleKey: styleKey,
        color: colorValue,
        addedId: addedId,
        addedTitle: addedTitle
      });

      if (!styleKey) {
        this.log('skipped: could not resolve a style from the add-to-cart payload', product);
        return;
      }

      if (added.id) this.addedProductIds.add(added.id);
      // What V2 shows under "Added to cart"; the line itself when the payload carried one.
      this.addedLine = Object.assign({}, line && typeof line === 'object' ? line : {}, { product_title: addedTitle });

      if (this.frequency === 'once_per_style' && this.shownStyles.has(styleKey)) {
        this.log('skipped: already offered this style this page view', styleKey);
        return;
      }

      this.returnFocusTo = addToCartBtn || null;
      this.addOriginCollection = this.originCollection(addToCartBtn, sourceEl);
      if (this.addOriginCollection) this.log('add came from collection', this.addOriginCollection);

      // Every synchronous reason to skip is behind us, so this add is ours to answer: hold the
      // drawer shut until we know whether a popup is coming. Held before any await, which is what
      // keeps the drawer from painting at all — the resolve below may be a network round-trip away.
      this.log('holding the cart drawer back');
      this.gate.hold(() => {
        this.log('gave up waiting — releasing the cart drawer');
        this.revealCart();
      });

      this.resolveItems(styleKey, colorValue, added).then((result) => {
        // A popup is already up, so the drawer stays shut either way — that popup's own close
        // reveals the cart.
        if (this.classList.contains('is-open')) {
          this.gate.release();
          return;
        }
        if (!result.items.length) {
          this.log('skipped: nothing to offer for this product');
          // The drawer was held back for a popup that never came — let it through.
          this.gate.release();
          this.revealCart();
          return;
        }
        this.open(result.items, styleKey, result.source);
      }).catch((err) => {
        this.log('failed to resolve anything to offer', err);
        this.gate.release();
        this.revealCart();
      });
    }

    /* Same colour is the primary source — other styles in the colour just added, matching
       the same_color_collection logic in snippets/product-complementary.liquid. The style
       swatch store is the fallback for colours with no collection behind them. */
    resolveItems(styleKey, colorValue, added) {
      const wantsColor = this.source !== 'same_style';

      if (this.isV2) {
        if (!colorValue) return Promise.resolve({ items: [], source: 'same_color' });
        return this.collectSameColor(colorValue, added).then((items) => ({ items: items, source: 'same_color' }));
      }

      if (wantsColor && colorValue) {
        return this.collectSameColor(colorValue, added).then((items) => {
          if (items.length) return { items: items, source: 'same_color' };
          this.log('no same-colour products — falling back to other colourways of this style');
          return this.collectStyleSiblingsAsync(styleKey, added);
        });
      }

      return this.collectStyleSiblingsAsync(styleKey, added);
    }

    collectStyleSiblingsAsync(styleKey, added) {
      const immediate = this.collectSiblings(styleKey, added);
      if (immediate.length) return Promise.resolve({ items: immediate, source: 'same_style' });

      // Swatch data may still be in flight on a fast add — wait briefly, then give up.
      this.log('no colourways yet — waiting for the swatch fetch');
      return this.waitForStyle(styleKey).then(() => ({
        items: this.collectSiblings(styleKey, added),
        source: 'same_style'
      }));
    }

    collectSameColor(colorValue, added) {
      const handle = normalizeColor(colorValue);
      if (!handle) return Promise.resolve([]);

      // The colour added plus whatever the merchant paired with it. The section builds the same
      // list in Liquid to filter the inline payload, so both paths accept the same colours.
      const ranks = relatedColorRanks(handle, this.colorPairs);
      this.log('colours this add will accept', Array.from(ranks.keys()));

      // Product pages inline this colour's products (see sections/upsell-popup.liquid), so the
      // common case resolves with no request at all.
      const inline = this.inlineColorNodes(handle);
      if (inline) {
        this.log('using the inline colour payload', { handle: handle, products: inline.length });
        return Promise.resolve(this.itemsFromNodes(inline, ranks, added));
      }

      return this.fetchSourceCollection().then((nodes) => {
        if (!nodes) return [];
        return this.itemsFromNodes(nodes, ranks, added);
      });
    }

    /* The inlined payload is for the colour of the page it was rendered on. An add for any other
       colour — a swatch switch, or a quick add from a collection — has to go out and fetch. */
    inlineColorNodes(handle) {
      if (!handle || normalizeColor(this.getAttribute('data-color-handle')) !== handle) return null;

      const source = this.querySelector('[data-upsell-source]');
      if (!source) return null;

      const nodes = Array.from(source.querySelectorAll('[data-upsell-product]'));
      return nodes.length ? nodes : null;
    }

    itemsFromNodes(nodes, ranks, added) {
      const items = [];
      // The popup lists one row per style, and a colour family can offer two colourways of the
      // same style — "Nightfall" and "Nightfall Gem" of one legging. Keep the closer colour.
      const byStyle = new Map();
      // relatedColorRanks() always seeds the colour added at rank 0, so it leads the map.
      const handle = ranks.keys().next().value || '';

      nodes.forEach((node) => {
        const productId = node.getAttribute('data-related-product-id') || '';
        if (added.id && productId === added.id) return;
        if (this.addedProductIds.has(String(productId))) return;
        // The inline payload already filters on colour; this applies the same rule to the fetched
        // collection view, which goes out unfiltered because Liquid cannot read a query string.
        const colorHandle = normalizeColor(node.getAttribute('data-color-values'));
        const colorRank = colorRankFor(colorHandle, ranks);
        if (colorRank === -1) return;

        const data = parseSwatchProductData(node);
        if (!data || !data.variants.length) return;
        if (data.title && data.title === added.title) return;
        if (!data.available && !this.showSoldOut) return;

        const styleTok = normalizeColor(data.styleTitle);
        const isExactColor = colorHandle === handle;
        // V2 offers exactly the colour added — no "Nightfall Gem" under "Nightfall".
        if (this.isV2 && !isExactColor) return;
        // renderItems() labels a row with its colour when it is not the colour just added — a
        // family match, or a paired colour, means the rows are no longer all one shade.
        data.isExactColor = isExactColor;
        data.colorRank = colorRank;
        const seen = byStyle.get(styleTok);

        if (seen) {
          // Closest colour wins the style's one row: a better rank outright, and within a rank
          // the exact colour over a family match.
          const closer = colorRank < seen.colorRank ||
            (colorRank === seen.colorRank && isExactColor && !seen.isExactColor);
          if (!closer) return;
          items[seen.index] = data;
          seen.colorRank = colorRank;
          seen.isExactColor = isExactColor;
          return;
        }

        byStyle.set(styleTok, { index: items.length, colorRank: colorRank, isExactColor: isExactColor });
        items.push(data);
      });

      // Rank first so the colour added always outranks the colours paired with it, then
      // alphabetically within a rank — which is the whole order when no pairs are configured.
      items.sort((a, b) =>
        (a.colorRank - b.colorRank) ||
        a.styleTitle.localeCompare(b.styleTitle, undefined, { sensitivity: 'base' }));
      this.log('same-colour products available', items.map(function (item) { return item.title; }));
      return items.slice(0, this.maxProducts);
    }

    /* The collection the popup draws from: the one the add came out of when the section named it,
       otherwise the section-level fallback resolved server-side (see sections/upsell-popup.liquid).
       It comes back with every product's colour on the node; itemsFromNodes() keeps the matches.

       One request per collection per page view — the cache is keyed by handle and holds the
       in-flight promise, so simultaneous adds from the same section share a single fetch. */
    fetchSourceCollection() {
      const handle = this.addOriginCollection || (this.getAttribute('data-source-collection') || '').trim();
      if (!handle) {
        this.log('no source collection to fetch from');
        return Promise.resolve(null);
      }

      this.sourceCache = this.sourceCache || new Map();
      if (this.sourceCache.has(handle)) return this.sourceCache.get(handle);

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = root + 'collections/' + encodeURIComponent(handle) + '?view=upsell';

      const request = fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((response) => {
          if (!response.ok) throw new Error('collection ' + handle + ' returned ' + response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nodes = Array.from(doc.querySelectorAll('[data-upsell-product]'));
          this.log('fetched source collection', { handle: handle, products: nodes.length });
          return nodes;
        })
        .catch((err) => {
          this.log('could not fetch the source collection', { handle: handle, error: err.message });
          return null;
        });

      this.sourceCache.set(handle, request);
      return request;
    }

    waitForStyle(styleKey) {
      return new Promise((resolve) => {
        let timer = null;

        const onUpdate = (event) => {
          const keys = (event.detail && event.detail.keys) || [];
          if (!keys.includes(styleKey)) return;
          window.clearTimeout(timer);
          document.removeEventListener('styleSwatches:updated', onUpdate);
          resolve();
        };

        timer = window.setTimeout(() => {
          document.removeEventListener('styleSwatches:updated', onUpdate);
          resolve();
        }, STYLE_READY_TIMEOUT);

        document.addEventListener('styleSwatches:updated', onUpdate);
      });
    }

    collectSiblings(styleKey, added) {
      const store = window.theme && window.theme.styleSwatchStore;
      if (!store) {
        this.log('style swatch store not published yet (new_swatch_logic has not run)');
        return [];
      }

      const entry = typeof store.get === 'function' ? store.get(styleKey) : null;
      if (!entry || !entry.htmlById || !entry.htmlById.size) {
        this.log('no store entry for style key', { styleKey: styleKey, known: Array.from(store.keys()) });
        return [];
      }

      const scratch = document.createElement('div');
      const items = [];

      entry.htmlById.forEach((html, productId) => {
        if (added.id && String(productId) === added.id) return;
        if (this.addedProductIds.has(String(productId))) return;

        scratch.innerHTML = html;
        const anchor = scratch.querySelector('a[data-related-product-id]');
        const data = parseSwatchProductData(anchor);
        if (!data || !data.variants.length) return;
        if (data.title && data.title === added.title) return;
        if (!data.available && !this.showSoldOut) return;

        items.push(data);
      });

      items.sort((a, b) => a.colorLabel.localeCompare(b.colorLabel, undefined, { sensitivity: 'base' }));
      this.log('colourways available', items.map(function (item) { return item.title; }));
      return items.slice(0, this.maxProducts);
    }

    /* ─── Rendering ───────────────────────────────────────────── */

    open(items, styleKey, source) {
      if (this.isV2) this.renderAdded(this.addedLine);
      this.renderItems(items, source);
      this.setError('');
      this.updateSubmitState();

      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('upsell-popup-open');
      if (styleKey) this.shownStyles.add(styleKey);

      // The drawer was held shut for this popup, so there is nothing to close and nothing left to
      // wait for — declining from here calls revealCart() instead. Adding from inside an already
      // open drawer is the one case with a drawer to get out of the way.
      this.gate.release();
      // Only when a cart drawer really is open: cart:close reaches theme.Drawers.close,
      // which re-focuses its opener 500ms later and would pull focus out of this dialog.
      // Both drawers have to be tested — #CartDrawer specifically for the stock one (the
      // search and nav drawers share the html.js-drawer-open flag), and <cart-drawer-v2>
      // for the custom one (settings.cart_drawer_style == 'custom'), which is the drawer
      // actually in use on this store.
      if (
        document.querySelector('#CartDrawer.drawer--is-open') ||
        document.querySelector('cart-drawer-v2.is-open')
      ) {
        document.dispatchEvent(new CustomEvent('cart:close'));
      }

      const focusTarget = this.querySelector('.upsell-popup__close');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    close() {
      if (!this.classList.contains('is-open')) return;

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('upsell-popup-open');

      // offsetParent is null for the quick-add modal's button once that modal has closed —
      // focusing a hidden element silently drops focus to <body>.
      const returnTo = this.returnFocusTo;
      if (returnTo && document.contains(returnTo) && returnTo.offsetParent !== null) {
        try {
          returnTo.focus({ preventScroll: true });
        } catch (err) {
          returnTo.focus();
        }
      }
    }

    revealCart() {
      const settings = (window.theme && window.theme.settings) || {};
      const routes = (window.theme && window.theme.routes) || {};

      if (settings.cartType === 'page' && routes.cartPage) {
        window.location = routes.cartPage;
        return;
      }

      // Deferred to the next task on purpose. theme.Drawers binds a window-level click
      // listener as it opens and closes on any click outside .drawer — opening the drawer
      // synchronously from a click handler means that very click, still bubbling, closes
      // it again. Impulse guards this with evt.stopPropagation() when a source event is
      // passed; cart:open carries none, so we wait for the event to finish dispatching.
      window.setTimeout(() => {
        this.log('opening cart drawer');
        document.dispatchEvent(new CustomEvent('cart:open'));
      }, 0);
    }

    renderItems(items, source) {
      // Rows are all one shade only when they exactly match the colour just added — then repeating
      // it per row is noise and the title carries the style instead. Every colourway row in
      // same_style mode, and every family or paired match in same_color mode ("Nightfall Gem"
      // under "Nightfall", "Milk Gem" under "Blue Steel"), needs its own label to be tellable apart.
      const labelEveryColor = source === 'same_style';
      this.list.innerHTML = '';

      items.forEach((data, index) => {
        const fragment = this.template.content.cloneNode(true);
        const item = fragment.querySelector('[data-upsell-item]');
        if (!item) return;

        item.setAttribute('data-product-id', data.id);
        item.setAttribute('data-product-variants', JSON.stringify(data.variants));
        item.setAttribute('data-product-options', JSON.stringify(data.options));
        item.setAttribute('data-option-indexes', JSON.stringify(data.optionIndexes));
        item.setAttribute('data-product-price', data.price);
        item.setAttribute('data-product-compare-price', data.comparePrice);
        item.setAttribute('data-product-price-varies', data.priceVaries);
        item.classList.toggle('upsell-popup__item--sold-out', !data.available);

        const checkbox = item.querySelector('[data-upsell-select]');
        const checkboxId = 'UpsellSelect-' + data.id + '-' + index;
        if (checkbox) {
          checkbox.id = checkboxId;
          checkbox.setAttribute('aria-label', data.title);
          checkbox.disabled = !data.available;
          const label = checkbox.closest('label');
          if (label) label.setAttribute('for', checkboxId);
        }

        const image = item.querySelector('[data-upsell-image]');
        const placeholder = item.querySelector('[data-upsell-placeholder]');
        if (image && data.imageFirst) {
          image.src = sizedImage(data.imageFirst, 220);
          image.alt = data.title;
          image.hidden = false;
          if (placeholder) placeholder.remove();
        }

        item.querySelectorAll('[data-upsell-product-link]').forEach((link) => {
          if (data.url) link.setAttribute('href', data.url);
        });

        const title = item.querySelector('[data-upsell-title]');
        if (title) title.textContent = data.styleTitle || data.title;

        const color = item.querySelector('[data-upsell-color]');
        const showColorLabel = this.isV2 || labelEveryColor || data.isExactColor === false;
        if (color && showColorLabel && data.colorLabel) {
          color.textContent = data.colorLabel;
          color.hidden = false;
        }

        this.renderPrice(item, data, null);
        this.renderOptions(item, data, this.defaultSelectedOptions(data));
        this.resolveItemVariant(item, data);

        this.list.appendChild(fragment);
      });
    }

    /* V2's "Added to cart" row, from the cart/add.js line: style name, "Colour / Size", unit price.
       Rows the payload could not fill (a bare add event with only a title) simply stay empty. */
    renderAdded(line) {
      if (!this.addedEl) return;
      line = line || {};

      const title = line.product_title || line.title || '';
      const titleEl = this.addedEl.querySelector('[data-upsell-added-title]');
      if (titleEl) titleEl.textContent = splitStyleTitle(title);

      const color = colorFromTitle(title);
      const variantTitle = line.variant_title && line.variant_title !== 'Default Title'
        ? line.variant_title.split(' / ').map(function (value) { return displayOptionText(value, 'size'); }).join(' / ')
        : '';
      const parts = [];
      if (color && variantTitle.toLowerCase().indexOf(color.toLowerCase()) === -1) parts.push(color);
      if (variantTitle) parts.push(variantTitle);
      const variantEl = this.addedEl.querySelector('[data-upsell-added-variant]');
      if (variantEl) {
        variantEl.textContent = parts.join(' / ');
        variantEl.hidden = !parts.length;
      }

      const price = line.final_price != null ? line.final_price : line.price;
      const priceEl = this.addedEl.querySelector('[data-upsell-added-price]');
      if (priceEl) priceEl.textContent = price != null ? formatMoney(price) : '';

      const imageUrl = line.image || (line.featured_image && line.featured_image.url) || '';
      const image = this.addedEl.querySelector('[data-upsell-added-image]');
      if (image) {
        image.hidden = !imageUrl;
        if (imageUrl) {
          image.src = sizedImage(imageUrl, 240);
          image.alt = (line.featured_image && line.featured_image.alt) || splitStyleTitle(title);
        } else {
          image.removeAttribute('src');
        }
      }
    }

    defaultSelectedOptions(data) {
      const firstAvailable = data.variants.find(function (variant) { return variant.available; }) || data.variants[0];
      return variantOptions(firstAvailable).slice();
    }

    renderPrice(item, data, variant) {
      const priceEl = item.querySelector('[data-upsell-price-current]');
      if (!priceEl) return;

      const price = variant && variant.price != null ? variant.price : data.price;
      const comparePrice = variant && variant.compare_at_price != null ? variant.compare_at_price : data.comparePrice;

      priceEl.textContent = !variant && data.priceVaries
        ? this.strings.fromText.replace('[price]', formatMoney(price))
        : formatMoney(price);

      const priceWrap = item.querySelector('[data-upsell-price]');
      if (!priceWrap) return;

      const original = priceWrap.querySelector('.grid-product__price--original');
      if (original) original.remove();

      if (comparePrice > price) {
        priceWrap.insertAdjacentHTML(
          'beforeend',
          '<span class="visually-hidden">' + this.strings.regularPrice + '</span>' +
            '<span class="grid-product__price--original">' + formatMoney(comparePrice) + '</span>'
        );
      }
    }

    renderOptions(item, data, selectedOptions) {
      const container = item.querySelector('[data-upsell-options]');
      if (!container) return;

      container.innerHTML = '';
      item.setAttribute('data-selected-options', JSON.stringify(selectedOptions));

      if (!data.optionIndexes.length) return;

      data.optionIndexes.forEach((optionIndex) => {
        const optionName = data.options[optionIndex] || this.strings.sizeLabel;
        const seen = new Set();
        const select = document.createElement('select');
        const selectId = 'UpsellSize-' + data.id + '-' + optionIndex;

        select.id = selectId;
        select.className = 'upsell-popup__size-select';
        select.setAttribute('data-upsell-option-select', '');
        select.setAttribute('data-option-index', optionIndex);

        data.variants.forEach((variant) => {
          const optionValue = variantOptions(variant)[optionIndex];
          if (!optionValue || seen.has(optionValue)) return;
          seen.add(optionValue);

          const availableForSelection = data.variants.some(function (candidate) {
            return (
              variantOptions(candidate)[optionIndex] === optionValue &&
              candidate.available &&
              variantMatchesSelectedOptions(candidate, selectedOptions, optionIndex)
            );
          });

          const option = document.createElement('option');
          option.value = optionValue;
          option.textContent = displayOptionText(optionValue, optionName);
          option.disabled = !availableForSelection;
          option.selected = selectedOptions[optionIndex] === optionValue;
          select.appendChild(option);
        });

        const label = document.createElement('label');
        label.className = 'visually-hidden';
        label.setAttribute('for', selectId);
        label.textContent = optionName;

        const row = document.createElement('div');
        row.className = 'upsell-popup__size-row';
        row.appendChild(label);
        row.appendChild(select);
        container.appendChild(row);
      });
    }

    resolveItemVariant(item, data) {
      const selectedOptions = parseJsonAttr(item, 'data-selected-options', []);
      const matching = data.variants.filter(function (variant) {
        return variantMatchesSelectedOptions(variant, selectedOptions);
      });
      const variant = matching.find(function (candidate) { return candidate.available; }) || null;

      const stock = item.querySelector('[data-upsell-stock]');
      const checkbox = item.querySelector('[data-upsell-select]');

      if (variant) {
        item.setAttribute('data-selected-variant-id', variant.id);
        item.classList.remove('upsell-popup__item--sold-out');
        if (stock) {
          stock.hidden = true;
          stock.textContent = '';
        }
        if (checkbox) checkbox.disabled = false;
      } else {
        item.removeAttribute('data-selected-variant-id');
        item.classList.add('upsell-popup__item--sold-out');
        if (stock) {
          stock.hidden = false;
          stock.textContent = this.strings.soldOut;
        }
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
        item.classList.remove('is-selected');
      }

      this.renderPrice(item, data, variant);
    }

    itemProductData(item) {
      return {
        id: item.getAttribute('data-product-id') || '',
        variants: parseJsonAttr(item, 'data-product-variants', []),
        options: parseJsonAttr(item, 'data-product-options', []),
        optionIndexes: parseJsonAttr(item, 'data-option-indexes', []),
        price: Number.parseInt(item.getAttribute('data-product-price') || '0', 10) || 0,
        comparePrice: Number.parseInt(item.getAttribute('data-product-compare-price') || '0', 10) || 0,
        priceVaries: (item.getAttribute('data-product-price-varies') || '').toLowerCase() === 'true'
      };
    }

    /* ─── Interaction ─────────────────────────────────────────── */

    onClick(event) {
      if (event.target.closest('[data-upsell-close]') || event.target === this.querySelector('.upsell-popup__overlay')) {
        event.preventDefault();
        this.close();
        this.revealCart();
        return;
      }

      if (event.target.closest('[data-upsell-submit]')) {
        event.preventDefault();
        this.submit();
        return;
      }

      // Tapping anywhere on a row toggles it — the checkbox alone is a small target on mobile.
      const item = event.target.closest('[data-upsell-item]');
      if (!item) return;
      if (event.target.closest('a, select, label, input')) return;

      const checkbox = item.querySelector('[data-upsell-select]');
      if (!checkbox || checkbox.disabled) return;

      checkbox.checked = !checkbox.checked;
      item.classList.toggle('is-selected', checkbox.checked);
      this.setError('');
      this.updateSubmitState();
    }

    onChange(event) {
      const select = event.target.closest('[data-upsell-option-select]');
      if (select) {
        const item = select.closest('[data-upsell-item]');
        const optionIndex = Number.parseInt(select.getAttribute('data-option-index') || '-1', 10);
        if (!item || !Number.isFinite(optionIndex) || optionIndex < 0) return;

        const data = this.itemProductData(item);
        const selectedOptions = parseJsonAttr(item, 'data-selected-options', []);
        selectedOptions[optionIndex] = select.value;

        this.renderOptions(item, data, selectedOptions);
        this.resolveItemVariant(item, data);
        this.setError('');
        this.updateSubmitState();
        return;
      }

      const checkbox = event.target.closest('[data-upsell-select]');
      if (!checkbox) return;

      const item = checkbox.closest('[data-upsell-item]');
      if (item) item.classList.toggle('is-selected', checkbox.checked);
      this.setError('');
      this.updateSubmitState();
    }

    // Escape closes on keyup, not keydown: theme.Drawers binds a window keyup listener as
    // it opens, so closing on keydown would let the same press's keyup shut the drawer again.
    onKeyup(event) {
      if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;
      this.close();
      this.revealCart();
    }

    onFocusIn(event) {
      if (!this.classList.contains('is-open') || !this.panel) return;
      if (this.panel.contains(event.target)) return;

      const focusTarget = this.querySelector('.upsell-popup__close');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (!this.classList.contains('is-open')) return;
      if (event.key !== 'Tab' || !this.panel) return;

      const focusable = Array.from(
        this.panel.querySelectorAll(
          "a[href], button:enabled, select:enabled, input:not([type=hidden]):enabled, [tabindex]:not([tabindex^='-'])"
        )
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    selectedItems() {
      return Array.from(this.querySelectorAll('[data-upsell-item]')).filter(function (item) {
        const checkbox = item.querySelector('[data-upsell-select]');
        return checkbox && checkbox.checked;
      });
    }

    updateSubmitState() {
      if (!this.submitBtn) return;

      const count = this.selectedItems().length;
      this.submitBtn.disabled = count === 0;
      this.submitBtn.classList.toggle('kit-button--disabled', count === 0);
      this.submitBtn.textContent = count > 0
        ? this.strings.addButtonWithCount.replace('[count]', count)
        : this.strings.addButton;
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    submit() {
      if (!this.submitBtn || this.submitBtn.disabled) return;

      const selected = this.selectedItems();
      const items = selected
        .map(function (item) {
          return {
            id: Number.parseInt(item.getAttribute('data-selected-variant-id') || '', 10),
            quantity: 1,
            properties: { [UPSELL_LINE_PROPERTY_KEY]: UPSELL_LINE_PROPERTY_VALUE }
          };
        })
        .filter(function (line) { return Number.isFinite(line.id) && line.id > 0; });

      if (!items.length) {
        this.setError(this.strings.errorIncomplete);
        return;
      }

      this.submitBtn.classList.add('kit-button--loading');
      this.submitBtn.disabled = true;
      this.setError('');

      const cartAddUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';

      fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) { return response.json(); })
        .then((data) => {
          if (data.status === 422) throw data;

          selected.forEach((item) => {
            const productId = item.getAttribute('data-product-id');
            if (productId) this.addedProductIds.add(String(productId));
          });

          this.close();

          // suppressUpsell keeps this from re-triggering itself; the cart drawer
          // still rebuilds and opens off the same event.
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: data,
              addToCartBtn: this.submitBtn,
              suppressUpsell: true
            }
          }));
        })
        .catch((errorData) => {
          const message = errorData && (errorData.description || errorData.message);
          this.setError(message || this.strings.errorAddFailed);
        })
        .finally(() => {
          this.submitBtn.classList.remove('kit-button--loading');
          this.updateSubmitState();
        });
    }
  }

  customElements.define('upsell-popup', UpsellPopup);
}
